/*
 * Copyright (c) 2023. Bolmate B.V.
 */

import {generateUUID, IndexedObject, nullify, ResponseData} from '@dvrd/dvr-controls';
import IDate from '@dvrd/idate';
import React, {ForwardedRef, forwardRef, useContext, useImperativeHandle, useState} from 'react';
import {
    ActivateHandle,
    FBBItem,
    Fulfilment,
    ProductCosts,
    SalesDashboardProduct,
    SendCostType
} from '../../util/interfaces';
import {AppContext} from '../misc/appContext';
import {deleteProductCosts, getProductCosts, updateProductCosts} from '../../util/requestSender';
import {createErrorMessage, parseMoney} from '../../util/utils';
import ProductCostsPopup from './productCosts';
import PageChangePermission from '../misc/pageChangePermission';
import {PermissionCallback, requestPageChangePermission} from '../../util/pageChanger';
import {showDialog, showSnackbar} from '@dvrd/dvr-controls';
import {useTranslation} from 'react-i18next';

interface Props {
    onReload: VoidFunction;
}

function miscCostsToArray(miscCosts: IndexedObject<number>): Array<{label: string; value: number}> {
    return Object.entries(miscCosts).map((entry: [string, number]) => ({label: entry[0], value: entry[1]}));
}

function miscCostsToObject(miscCosts: Array<{label: string; value: number}>): IndexedObject<number> {
    const miscCostsObj: IndexedObject<number> = {};
    for (const miscCost of miscCosts) miscCostsObj[miscCost.label] = miscCost.value;
    return miscCostsObj;
}

type LoadOptions = {product?: SalesDashboardProduct | null; fulfilment?: Fulfilment};

function ProductCostsController(props: Props, ref: ForwardedRef<ActivateHandle>) {
    const {account} = useContext(AppContext).accountContext;
    const {t} = useTranslation();
    const {onReload} = props;
    const [productCostsList, setProductCostsList] = useState<Array<ProductCosts>>([]);
    const [active, setActive] = useState(false);
    const [selectedProductCosts, setSelectedProductCosts] = useState<ProductCosts | null>(null);
    const [loading, setLoading] = useState(false);
    const [product, setProduct] = useState<SalesDashboardProduct | null>(null);
    const [listTab, setListTab] = useState<Fulfilment>(Fulfilment.BOTH);
    const [changed, setChanged] = useState(false);

    const groupedFBBItems: Record<string, Array<FBBItem>> = {
        '': [
            {
                value: '',
                label: t('productCosts.choosePackageSize', 'Kies pakketmaat'),
                fulfilmentCosts: 0,
                sendCosts: 0,
                selectable: false
            }
        ],
        low_price: [
            {value: '<16xxxs', label: 'XXXS (Max. 23,5 x 16,5 x 3 cm / 1kg)', fulfilmentCosts: 1.51, sendCosts: 1.04},
            {value: '<16xxs', label: 'XXS (Max. 37,5 x 26 x 3 cm / 2kg)', fulfilmentCosts: 1.61, sendCosts: 1.69},
            {value: '<16xs', label: 'XS (Max. 37,5 x 26 x 5 cm / 5kg)', fulfilmentCosts: 1.78, sendCosts: 2.52},
            {value: '<16s', label: 'S (Max. 45 x 30 x 8 cm / 5kg)', fulfilmentCosts: 2.23, sendCosts: 3.05},
            {value: '<16m', label: 'M (Max. 55 x 35 x 20 cm / 8kg)', fulfilmentCosts: 2.36, sendCosts: 3.09},
            {value: '<16l', label: 'L (Max. 72 x 50 x 41 cm / 15kg)', fulfilmentCosts: 3.15, sendCosts: 3.43}
        ],
        normal_price: [
            {value: 'xxxs', label: 'XXXS (Max. 23,5 x 16,5 x 3 cm / 1kg)', fulfilmentCosts: 1.51, sendCosts: 1.24},
            {value: 'xxs', label: 'XXS (Max. 37,5 x 26 x 3 cm / 2kg)', fulfilmentCosts: 1.61, sendCosts: 1.89},
            {value: 'xs', label: 'XS (Max. 37,5 x 26 x 5 cm / 5kg)', fulfilmentCosts: 1.78, sendCosts: 3.27},
            {value: 's', label: 'S (Max. 45 x 30 x 8 cm / 5kg)', fulfilmentCosts: 2.23, sendCosts: 3.8},
            {value: 'm', label: 'M (Max. 55 x 35 x 20 cm / 8kg)', fulfilmentCosts: 2.36, sendCosts: 3.84},
            {value: 'l', label: 'L (Max. 72 x 50 x 41 cm / 15kg)', fulfilmentCosts: 3.15, sendCosts: 4.18},
            {value: 'xl', label: 'XL (Max. 100 x 70 x 55 cm / 30kg', fulfilmentCosts: 4.83, sendCosts: 4.27},
            {value: 'xxl', label: 'XXL (Max. 175 x 78 x 58 cm / 31,5kg', fulfilmentCosts: 6.4, sendCosts: 4.4},
            {value: '3xl1', label: '3XL 1 mans (Max. 240 x 110 cm / 31,5kg', fulfilmentCosts: 7.86, sendCosts: 19.77},
            {value: '3xl2', label: '3XL 2 mans (Max. 400 x 200 cm / 63kg', fulfilmentCosts: 7.86, sendCosts: 56.63}
        ]
    };

    function getNextMiscLabel(miscCosts: IndexedObject<number>): string {
        let count = 0;
        let label = t('productCosts.miscCostsLabel', 'Overige kosten');
        while (Object.hasOwn(miscCosts, label)) {
            count++;
            label = `${t('productCosts.miscCostsLabel', 'Overige kosten')} ${count}`;
        }
        return label;
    }

    function onActivate(product: SalesDashboardProduct) {
        setProduct(product);
        setActive(true);
        loadCosts({product});
    }

    function onClose() {
        requestPageChangePermission(() => {
            setActive(false);
            setProduct(null);
            setProductCostsList([]);
            setSelectedProductCosts(null);
            setChanged(false);
        });
    }

    function onChangeListTab(fulfilment: Fulfilment) {
        setListTab(fulfilment);
        loadCosts({fulfilment});
    }

    function onBack() {
        requestPageChangePermission(() => {
            setSelectedProductCosts(null);
            setChanged(false);
        });
    }

    function onSelectProductCosts(costs: ProductCosts) {
        return function () {
            setSelectedProductCosts(costs);
        };
    }

    function onChangeFulfilment(fulfilment: Fulfilment) {
        if (!selectedProductCosts) return;
        setSelectedProductCosts({...selectedProductCosts, fulfilment, fulfilment_cost: 0, send_cost: 0});
    }

    function onChangeProductCosts(key: keyof ProductCosts) {
        return function (value: any) {
            if (!selectedProductCosts) return;
            setSelectedProductCosts({...selectedProductCosts, [key]: value});
            setChanged(true);
        };
    }

    function onChangeMiscCost(index: number, key: 'label' | 'value', per: 'product' | 'order') {
        return function (value: string | number) {
            const selectedCosts = Object.assign({}, selectedProductCosts);
            const jsonPropKey = per === 'product' ? 'misc_costs_per_item' : 'misc_costs_per_order';
            const miscCosts = per === 'product' ? selectedCosts.miscCostsPerItem : selectedCosts.miscCostsPerOrder;
            if (key === 'value') miscCosts[index].value = value as number;
            else miscCosts[index].label = value as string;
            selectedCosts[jsonPropKey] = miscCostsToObject(miscCosts);
            setSelectedProductCosts(selectedCosts);
            setChanged(true);
        };
    }

    function onAddMiscCost(per: 'product' | 'order') {
        return function () {
            if (!selectedProductCosts) return;
            const selected = Object.assign({}, selectedProductCosts);
            const jsonPropKey = per === 'product' ? 'misc_costs_per_item' : 'misc_costs_per_order';
            const jsPropKey = per === 'product' ? 'miscCostsPerItem' : 'miscCostsPerOrder';
            const label = getNextMiscLabel(selected[jsonPropKey]);
            selected[jsonPropKey][label] = 0;
            selected[jsPropKey] = miscCostsToArray(selected[jsonPropKey]);
            setSelectedProductCosts(selected);
            setChanged(true);
            window.setTimeout(() => {
                const element = document.getElementById('misc-costs-container');
                if (!element) return;
                element.scrollTo({top: element.scrollHeight});
            });
        };
    }

    function onAddProductCosts() {
        const fulfilment = listTab === Fulfilment.BOTH ? Fulfilment.FBR : listTab;
        setSelectedProductCosts({
            id: null,
            ean: product?.ean ?? '',
            start_date: IDate.now().format('YYYY-MM-DD'),
            end_date: null,
            fulfilment: fulfilment,
            cost_price: 0,
            fulfilment_cost: 0,
            send_cost: 0,
            send_cost_type: SendCostType.ORDER,
            misc_costs_per_item: {},
            misc_costs_per_order: {},
            miscCostsPerItem: [],
            miscCostsPerOrder: [],
            internalID: generateUUID()
        });
        setChanged(true);
    }

    function onDeleteProductCosts(productCosts: ProductCosts) {
        return function () {
            if (!account || !productCosts.id) return;
            setLoading(true);
            deleteProductCosts({
                account_id: account.id,
                costs_id: productCosts.id,
                callback: (data: ResponseData) => {
                    if (data.success) {
                        showSnackbar(t('productCosts.deleted', 'De kosten zijn verwijderd'));
                        onReload();
                        loadCosts();
                    } else {
                        setLoading(false);
                        showDialog(
                            createErrorMessage(
                                data.message ??
                                    t(
                                        'productCosts.deleteFailedMessage',
                                        'Het verwijderen van de kosten is niet gelukt.'
                                    )
                            ),
                            t('productCosts.deleteFailed', 'Verwijderen mislukt')
                        );
                    }
                }
            });
        };
    }

    function onSelectFBBItem(value: string) {
        if (!selectedProductCosts) return;
        const _selected = Object.assign({}, selectedProductCosts);
        const allItems: Array<FBBItem> = Object.values(groupedFBBItems).reduce(
            (current: Array<FBBItem>, next: Array<FBBItem>) => current.concat(next),
            []
        );
        for (const fbbItem of allItems) {
            if (fbbItem.value !== value) continue;
            _selected.fulfilment_cost = fbbItem.fulfilmentCosts;
            _selected.send_cost = fbbItem.sendCosts;
            break;
        }
        setSelectedProductCosts(_selected);
        setChanged(true);
    }

    function onDeleteMiscCost(index: number, per: 'product' | 'order') {
        return function () {
            if (!selectedProductCosts) return;
            const selected = Object.assign({}, selectedProductCosts);
            const jsonPropKey = per === 'product' ? 'misc_costs_per_item' : 'misc_costs_per_order';
            const jsPropKey = per === 'product' ? 'miscCostsPerItem' : 'miscCostsPerOrder';
            selected[jsPropKey].splice(index, 1);
            selected[jsonPropKey] = miscCostsToObject(selected[jsPropKey]);
            setSelectedProductCosts(selected);
            setChanged(true);
        };
    }

    function onBlurCurrency(key: keyof ProductCosts) {
        return function () {
            if (!selectedProductCosts) return;
            const currentValue = selectedProductCosts[key];
            onChangeProductCosts(key)(parseMoney(currentValue as number, false, false, true));
            setChanged(true);
        };
    }

    function onDenyPageChange(id: number | null, callback: PermissionCallback) {
        if (changed)
            onSubmit(() => {
                callback(id, true);
            })();
        else callback(id, true);
    }

    function onSubmit(callback?: VoidFunction) {
        return function () {
            if (!selectedProductCosts || !account) return;
            setLoading(true);
            updateProductCosts({
                data: {
                    account_id: account.id,
                    cost_price: selectedProductCosts.cost_price,
                    costs_id: nullify(selectedProductCosts.id),
                    ean: selectedProductCosts.ean,
                    fulfilment_cost: selectedProductCosts.fulfilment_cost,
                    misc_costs_per_item: miscCostsToObject(selectedProductCosts.miscCostsPerItem),
                    misc_costs_per_order: miscCostsToObject(selectedProductCosts.miscCostsPerOrder),
                    send_cost: selectedProductCosts.send_cost,
                    send_cost_type: selectedProductCosts.send_cost_type,
                    fulfilment: selectedProductCosts.fulfilment,
                    start_date: new IDate(selectedProductCosts.start_date).format('YYYY-MM-DD')
                },
                callback: (data: ResponseData) => {
                    if (data.success) {
                        loadCosts();
                        showSnackbar(t('productCosts.saved', 'De kosten zijn opgeslagen'));
                        onReload();
                        setChanged(false);
                        callback?.();
                    } else {
                        setLoading(false);
                        showDialog(
                            createErrorMessage(
                                data.message ??
                                    t('productCosts.saveFailedMessage', 'Het opslaan van de kosten is niet gelukt.')
                            ),
                            t('productCosts.saveFailed', 'Opslaan mislukt')
                        );
                    }
                }
            });
        };
    }

    function loadCosts(options?: LoadOptions) {
        const _product = options?.product ?? product;
        if (!account || !_product) return;
        setLoading(true);
        const fulfilment = options?.fulfilment ?? listTab;
        getProductCosts({
            account_id: account.id,
            ean: _product.ean,
            fulfilment,
            callback: (data: ResponseData) => {
                setLoading(false);
                if (data.success) {
                    const productCosts = data.product_costs.map((costs: ProductCosts) => {
                        costs.miscCostsPerItem = miscCostsToArray(costs.misc_costs_per_item);
                        costs.miscCostsPerOrder = miscCostsToArray(costs.misc_costs_per_order);
                        costs.internalID = generateUUID();
                        costs.id = costs.id?.toString() ?? null;
                        return costs;
                    });
                    setProductCostsList(productCosts);
                    setSelectedProductCosts(null);
                } else
                    showDialog(
                        createErrorMessage(
                            data.message ??
                                t('productCosts.loadFailedMessage', 'Het laden van de kosten is niet gelukt.')
                        ),
                        t('productCosts.loadFailed', 'Laden mislukt')
                    );
            }
        });
    }

    useImperativeHandle(ref, () => ({activate: onActivate}));

    return (
        <>
            <ProductCostsPopup
                onBack={onBack}
                onClose={onClose}
                onSelectProductCosts={onSelectProductCosts}
                onChangeProductCosts={onChangeProductCosts}
                onSubmit={onSubmit()}
                onAddProductCosts={onAddProductCosts}
                onDeleteProductCosts={onDeleteProductCosts}
                onChangeMiscCost={onChangeMiscCost}
                onAddMiscCost={onAddMiscCost}
                onSelectFBBItem={onSelectFBBItem}
                onDeleteMiscCost={onDeleteMiscCost}
                onChangeFulfilment={onChangeFulfilment}
                onChangeListTab={onChangeListTab}
                onBlurCurrency={onBlurCurrency}
                productCostsList={productCostsList}
                active={active}
                product={product}
                selectedProductCosts={selectedProductCosts}
                loading={loading}
                fbbItems={groupedFBBItems}
                listTab={listTab}
            />
            <PageChangePermission
                changed={changed}
                onDeny={onDenyPageChange}
                denyText={t('productCosts.pageChange.deny', 'Ja, wijzigingen opslaan')}
                allowText={t('productCosts.pageChange.allow', 'Nee, sluiten')}
                allowSecondary={true}
            >
                {t(
                    'productCosts.pageChange.body',
                    'De wijzigingen gaan verloren als je doorgaat. Wil je de wijzigingen eerst opslaan?'
                )}
            </PageChangePermission>
        </>
    );
}

export default forwardRef<ActivateHandle, Props>(ProductCostsController);
